import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend,
} from 'recharts'
import { authFetchJson } from '../lib/authFetch'
import { KPICard } from '../components/KPICard'
import { useTranslation } from '../hooks/useTranslation'
import { getDomainConfig, getAllDomains, type Domain } from '../lib/domainRegistry'
import { getPipelineStageConfig } from '../lib/pipelineRegistry'

// ─── API Types ────────────────────────────────────────────────────────────────

interface DashboardKPI {
  total_projects: number
  in_pipeline: number
  avg_score: number | null
  score_coverage: number
  institutions: number
  active_reviews: number
}

interface PipelineBoardData {
  stage: string
  count: number
}

interface DomainBreakdownItem {
  domain: Domain
  count: number
}

interface ScoreBucket {
  range: string
  count: number
}

// ─── Constants ────────────────────────────────────────────────────────────────

type Perspective = 'projects' | 'institutions'

const TOOLTIP_STYLE = {
  backgroundColor: '#1a1a2e',
  border: '1px solid #2a2a44',
  borderRadius: '6px',
  color: '#e8e8f0',
  fontSize: '13px',
}

const CHART_COLORS = {
  tick: '#a8a8c0',
  gridLine: '#2a2a44',
}

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 animate-pulse">
      <div className="h-6 bg-border-subtle rounded w-16 mb-2" />
      <div className="h-3 bg-border-standard rounded w-24" />
    </div>
  )
}

function SkeletonChart() {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 animate-pulse">
      <div className="h-4 bg-border-subtle rounded w-40 mb-4" />
      <div className="h-48 bg-border-standard rounded" />
    </div>
  )
}

// ─── Chart Card Wrapper ───────────────────────────────────────────────────────

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4">
      <h3 className="text-[13px] font-semibold text-text-primary mb-3">{title}</h3>
      {children}
    </div>
  )
}

// ─── Pipeline Funnel (CSS bars matching v2-mockup) ────────────────────────────

function PipelineFunnel({ data }: { data: PipelineBoardData[] }) {
  const { t } = useTranslation()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-text-secondary h-[200px]">
        {t('dashboard.no_data')}
      </div>
    )
  }

  const maxCount = Math.max(...data.map((d) => d.count))

  return (
    <div className="space-y-2">
      {data.map((item) => {
        const config = getPipelineStageConfig(item.stage)
        const pct = maxCount > 0 ? Math.max((item.count / maxCount) * 100, 5) : 0
        return (
          <div key={item.stage} className="flex items-center gap-2">
            <span className="w-[100px] text-caption text-text-secondary text-right shrink-0">
              {config?.label || item.stage}
            </span>
            <div className="flex-1 h-[22px] bg-bg-input rounded-sm overflow-hidden">
              <div
                className="h-full rounded-sm flex items-center pl-2 text-[10px] font-semibold text-white min-w-[30px] transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  backgroundColor: config?.color || '#6B7280',
                }}
              >
                {item.count}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Activity Timeline Placeholder ────────────────────────────────────────────

function ActivityTimelinePlaceholder() {
  const bars = [20, 35, 45, 30, 55, 70, 40, 50, 85, 65, 90, 100]
  return (
    <div>
      <div className="flex items-end gap-[2px] h-[120px] px-1">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm"
            style={{
              height: `${h}%`,
              backgroundColor: '#E61B25',
              opacity: 0.3 + (i / bars.length) * 0.7,
            }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-text-tertiary px-1 mt-1">
        <span>Jan</span>
        <span>Apr</span>
        <span>Jul</span>
        <span>Oct</span>
        <span>Dec</span>
      </div>
    </div>
  )
}

// ─── Domain Selector Bar ──────────────────────────────────────────────────────

interface DomainSelectorProps {
  selectedDomain: Domain | null
  onChange: (domain: Domain | null) => void
}

function DomainSelector({ selectedDomain, onChange }: DomainSelectorProps) {
  const { t } = useTranslation()
  const domains = getAllDomains()

  return (
    <div className="mb-6 flex flex-wrap gap-2">
      <button
        onClick={() => onChange(null)}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          selectedDomain === null
            ? 'bg-[#555] text-white'
            : 'bg-bg-surface border border-border-subtle text-text-primary hover:bg-bg-surface-hover'
        }`}
      >
        {t('common.all')}
      </button>
      {domains.map((domain) => {
        const config = getDomainConfig(domain)
        if (!config) return null
        return (
          <button
            key={domain}
            onClick={() => onChange(domain)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              selectedDomain === domain
                ? 'text-white border-transparent'
                : 'bg-bg-surface border border-border-subtle text-text-primary hover:bg-bg-surface-hover'
            }`}
            style={selectedDomain === domain ? { backgroundColor: config.color } : {}}
          >
            <span>{config.icon}</span>
            <span>{config.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// ─── Perspective Tabs ─────────────────────────────────────────────────────────

interface PerspectiveTabsProps {
  active: Perspective
  onChange: (p: Perspective) => void
}

function PerspectiveTabs({ active, onChange }: PerspectiveTabsProps) {
  const { t } = useTranslation()
  return (
    <div className="border-b border-border-standard mb-6">
      <div className="flex">
        {([
          { key: 'projects', labelKey: 'dashboard.tab_projects' },
          { key: 'institutions', labelKey: 'dashboard.tab_institutions' },
        ] as { key: Perspective; labelKey: string }[]).map(({ key, labelKey }) => (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={`px-4 py-3 text-body font-medium border-b-2 transition-colors duration-200 ${
              active === key
                ? 'text-text-primary border-primary-red'
                : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-bg-surface-hover'
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Error Banner ─────────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  const { t } = useTranslation()
  return (
    <div className="bg-error/10 border border-error/30 rounded-md px-4 py-3 text-error text-body mb-6">
      {t('dashboard.error_loading', { message })}
    </div>
  )
}

// ─── Dashboard Page ───────────────────────────────────────────────────────────

interface DashboardState {
  kpi: DashboardKPI | null
  pipelineData: PipelineBoardData[]
  scoreBuckets: ScoreBucket[]
  domainBreakdown: DomainBreakdownItem[]
  loading: boolean
  error: string | null
}

export function DashboardPage() {
  const { t } = useTranslation()
  const [perspective, setPerspective] = useState<Perspective>('projects')
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null)
  const [state, setState] = useState<DashboardState>({
    kpi: null,
    pipelineData: [],
    scoreBuckets: [],
    domainBreakdown: [],
    loading: true,
    error: null,
  })

  const fetchAll = useCallback(async (_p: Perspective, domain: Domain | null) => {
    setState((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const domainQuery = domain ? `?domain=${domain}` : ''

      let kpi: DashboardKPI | null = null
      try {
        kpi = await authFetchJson<DashboardKPI>(`/api/v2/dashboard/kpi${domainQuery}`)
      } catch (err) {
        console.error('KPI fetch failed:', err)
        kpi = {
          total_projects: 0,
          in_pipeline: 0,
          avg_score: null,
          score_coverage: 0,
          institutions: 0,
          active_reviews: 0,
        }
      }

      let pipelineData: PipelineBoardData[] = []
      try {
        const boardData = await authFetchJson<{ stages: Array<{ stage: string; count: number }> }>(
          `/api/v2/pipeline/board${domainQuery}`,
        )
        pipelineData = boardData.stages || []
      } catch {
        pipelineData = []
      }

      let domainBreakdown: DomainBreakdownItem[] = []
      if (!domain) {
        try {
          const domainData = await authFetchJson<Record<string, number>>(
            '/api/v2/dashboard/domains',
          )
          domainBreakdown = Object.entries(domainData || {}).map(([key, count]) => ({
            domain: key as Domain,
            count,
          }))
        } catch {
          domainBreakdown = []
        }
      }

      let scoreBuckets: ScoreBucket[] = []
      try {
        const projectsResp = await authFetchJson<{ data: Array<{ score_composite: number | null }> }>(
          `/api/v2/projects?fields=score_composite&limit=1000${domain ? `&domain=${domain}` : ''}`,
        )
        const scores = (projectsResp.data || [])
          .map((p) => p.score_composite)
          .filter((s): s is number => s !== null && s > 0)

        if (scores.length > 0) {
          const buckets = [
            { range: '0.0 – 0.2', min: 0, max: 0.2, count: 0 },
            { range: '0.2 – 0.4', min: 0.2, max: 0.4, count: 0 },
            { range: '0.4 – 0.6', min: 0.4, max: 0.6, count: 0 },
            { range: '0.6 – 0.8', min: 0.6, max: 0.8, count: 0 },
            { range: '0.8 – 1.0', min: 0.8, max: 1.01, count: 0 },
          ]
          for (const s of scores) {
            const bucket = buckets.find((b) => s >= b.min && s < b.max)
            if (bucket) bucket.count++
          }
          scoreBuckets = buckets.map(({ range, count }) => ({ range, count }))
        }
      } catch (err) {
        console.error('Score distribution fetch failed:', err)
      }

      setState({
        kpi,
        pipelineData,
        scoreBuckets,
        domainBreakdown,
        loading: false,
        error: null,
      })
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }))
    }
  }, [])

  useEffect(() => {
    void fetchAll(perspective, selectedDomain)
  }, [perspective, selectedDomain, fetchAll])

  const { kpi, pipelineData, scoreBuckets, domainBreakdown, loading, error } = state

  // Transform domain breakdown for chart
  const domainChartData = domainBreakdown.map((item) => ({
    domain: getDomainConfig(item.domain)?.label || item.domain,
    count: item.count,
    fill: getDomainConfig(item.domain)?.color || '#666',
  }))

  return (
    <div className="p-8 max-w-[1920px]">
      {/* Perspective tabs */}
      <PerspectiveTabs active={perspective} onChange={setPerspective} />

      {/* Domain selector */}
      <DomainSelector selectedDomain={selectedDomain} onChange={setSelectedDomain} />

      {/* Error */}
      {error && <ErrorBanner message={error} />}

      {/* KPI Row — 6-column grid matching v2-mockup */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        {loading || !kpi ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <KPICard
              label={t('dashboard.kpi.total_projects')}
              value={kpi.total_projects}
            />
            <KPICard
              label={t('dashboard.kpi.in_pipeline')}
              value={kpi.in_pipeline}
            />
            <KPICard
              label={t('dashboard.kpi.avg_score')}
              value={kpi.avg_score !== null ? kpi.avg_score.toFixed(2) : '\u2014'}
            />
            <KPICard
              label={t('dashboard.kpi.score_coverage')}
              value={`${kpi.score_coverage}%`}
            />
            <KPICard
              label={t('dashboard.kpi.institutions')}
              value={kpi.institutions}
            />
            <KPICard
              label={t('dashboard.kpi.active_reviews')}
              value={kpi.active_reviews}
            />
          </>
        )}
      </div>

      {/* Charts grid — 2-column layout matching v2-mockup */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonChart key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pipeline Funnel — CSS bars */}
          <ChartCard title={t('dashboard.chart.pipeline_funnel')}>
            <PipelineFunnel data={pipelineData} />
          </ChartCard>

          {/* Domain Breakdown (donut — only for "All" view) */}
          {!selectedDomain && (
            <ChartCard title={t('dashboard.chart.domain_breakdown')}>
              {domainChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={domainChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="count"
                      nameKey="domain"
                      paddingAngle={2}
                      isAnimationActive={false}
                    >
                      {domainChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value, name) => [
                        `${Number(value).toLocaleString()} projects`,
                        name,
                      ]}
                    />
                    <Legend
                      formatter={(value: string) => (
                        <span style={{ color: CHART_COLORS.tick, fontSize: '12px' }}>
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center text-text-secondary h-[250px]">
                  {t('dashboard.no_data')}
                </div>
              )}
            </ChartCard>
          )}

          {/* Score Distribution */}
          <ChartCard title={t('dashboard.chart.score_distribution')}>
            {scoreBuckets.length > 0 && scoreBuckets.some((b) => b.count > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={scoreBuckets}
                  margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.gridLine}
                  />
                  <XAxis
                    dataKey="range"
                    tick={{ fill: CHART_COLORS.tick, fontSize: 10 }}
                    axisLine={{ stroke: CHART_COLORS.gridLine }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: CHART_COLORS.tick, fontSize: 10 }}
                    axisLine={{ stroke: CHART_COLORS.gridLine }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    formatter={(value) => [
                      Number(value).toLocaleString(),
                      'Count',
                    ]}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className="flex flex-col items-center justify-center text-text-secondary gap-2"
                style={{ height: 200 }}
              >
                <span className="text-body">{t('dashboard.score_empty_title')}</span>
                <span className="text-caption-xs text-text-tertiary">
                  {t('dashboard.score_empty_hint')}
                </span>
              </div>
            )}
          </ChartCard>

          {/* Activity Timeline — CSS placeholder */}
          <ChartCard title={t('dashboard.chart.activity_timeline')}>
            <ActivityTimelinePlaceholder />
          </ChartCard>
        </div>
      )}
    </div>
  )
}
